import React, { useState } from "react";
import PracticeLayout from "../../../components/PracticeLayout";
import PracticeSection from "../../../components/PracticeSection";
import { Button } from "../../../components/ui/button";
import { toast } from "sonner";
import useDocumentTitle from "../../../hooks/useDocumentTitle";

export default function LevelBrowser() {
  useDocumentTitle("Level 6 · Browser Handling — Practice");
  const [alertMsg, setAlertMsg] = useState("");
  const [confirmMsg, setConfirmMsg] = useState("");
  const [promptMsg, setPromptMsg] = useState("");
  const [permissionState, setPermissionState] = useState("unknown");

  const showAlert = () => {
    window.alert("This is a native browser alert!");
    setAlertMsg("alert dismissed at " + new Date().toLocaleTimeString());
  };
  const showConfirm = () => {
    const ok = window.confirm("Do you want to proceed with this action?");
    setConfirmMsg(ok ? "confirmed" : "cancelled");
  };
  const showPrompt = () => {
    const val = window.prompt("What's your favourite test framework?", "Playwright");
    setPromptMsg(val === null ? "prompt cancelled" : `answered: ${val}`);
  };

  const openPopup = () => window.open("https://example.com", "practicePopup", "width=500,height=400");
  const openNewTab = () => window.open("/practice", "_blank", "noopener,noreferrer");

  const askNotification = async () => {
    if (!("Notification" in window)) { setPermissionState("unsupported"); return; }
    const p = await Notification.requestPermission();
    setPermissionState(p);
    if (p === "granted") new Notification("Practice granted 🎉", { body: "You're now allowed to send notifications." });
  };

  const downloadTxt = () => {
    const content = `QaTestology practice download\ngenerated: ${new Date().toISOString()}\n\nAssert the file's name and size in your test.`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "practice-download.txt";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("File downloaded — check your Downloads folder.");
  };

  return (
    <PracticeLayout
      eyebrow="Level 6 · Browser Handling"
      title="Everything that leaves the DOM"
      subtitle="Alerts, popups, permissions, downloads — the flows that trip up test frameworks the most."
      seo={{
        title: "Level 6 · Browser Handling — Alerts, Popups, Downloads",
        description: "Practise native alerts, confirm & prompt dialogs, popup windows, multiple tabs, notification permissions and file downloads.",
        keywords: "selenium alert practice, playwright popup, browser tab automation, download testing",
        image: "/og/og-browser.svg",
      }}
    >
      <PracticeSection id="alerts" title="Native alerts" hint="Playwright: page.on('dialog', d => d.accept())">
        <div className="flex flex-wrap gap-2">
          <Button onClick={showAlert} data-testid="btn-alert" className="bg-[#002FA7] hover:bg-[#00227A] text-white">alert()</Button>
          <Button onClick={showConfirm} data-testid="btn-confirm" variant="outline">confirm()</Button>
          <Button onClick={showPrompt} data-testid="btn-prompt" variant="outline">prompt()</Button>
        </div>
        <ul className="tag-mono text-[11px] text-zinc-500 mt-3 space-y-0.5">
          <li data-testid="alert-log">alert: {alertMsg || "—"}</li>
          <li data-testid="confirm-log">confirm: {confirmMsg || "—"}</li>
          <li data-testid="prompt-log">prompt: {promptMsg || "—"}</li>
        </ul>
      </PracticeSection>

      <PracticeSection id="popup" title="Popup window" hint="Playwright: waitForEvent('popup')">
        <Button onClick={openPopup} data-testid="btn-open-popup" className="bg-[#002FA7] hover:bg-[#00227A] text-white">
          Open popup (500×400)
        </Button>
      </PracticeSection>

      <PracticeSection id="new-tab" title="Multiple tabs / new window" hint="Assert on the new context's URL.">
        <div className="flex flex-wrap gap-2">
          <a
            href="/practice/api"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-new-tab"
            className="tag-mono inline-flex items-center h-10 px-4 rounded-md border border-zinc-300 hover:border-[#002FA7] hover:text-[#002FA7] text-sm"
          >
            Open API playground in new tab
          </a>
          <Button onClick={openNewTab} data-testid="btn-window-open" variant="outline">
            window.open(/practice)
          </Button>
        </div>
      </PracticeSection>

      <PracticeSection id="permissions" title="Permissions" hint="Playwright: context.grantPermissions(['notifications'])">
        <Button onClick={askNotification} data-testid="btn-permission" className="bg-[#002FA7] hover:bg-[#00227A] text-white">
          Request notification permission
        </Button>
        <div className="tag-mono text-[11px] text-zinc-500 mt-2" data-testid="permission-state">
          state: {permissionState}
        </div>
      </PracticeSection>

      <PracticeSection id="downloads" title="Downloads" hint="Playwright: waitForEvent('download')">
        <Button onClick={downloadTxt} data-testid="btn-download" className="bg-[#002FA7] hover:bg-[#00227A] text-white">
          Download practice-download.txt
        </Button>
      </PracticeSection>
    </PracticeLayout>
  );
}
